import { MouseEvent } from 'react'
import { Check, Copy, Maximize2, Pencil, RotateCcw, X } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface ArtifactChromeActionsProps {
  accessibleName: string
  hasCopied?: boolean
  /**
   * Owner-only edit. Hidden when `false`, or when `onEdit` is omitted so
   * existing Copy+Expand cards stay unchanged.
   */
  canEdit?: boolean
  /** When true, Edit is replaced by Done / Cancel / optional Revert. */
  isEditing?: boolean
  onEdit?: () => void
  onDone?: () => void
  onCancel?: () => void
  onRevert?: () => void
  onCopy?: () => void
  onExpand?: () => void
  /** Defaults to `artifact-chrome` (`artifact-chrome-edit` / `-copy` / `-expand`). */
  testIdPrefix?: string
  className?: string
}

const ICON_BUTTON_CLASS =
  'shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground'

export function ArtifactChromeActions(props: ArtifactChromeActionsProps) {
  const {
    accessibleName,
    hasCopied = false,
    canEdit = true,
    isEditing = false,
    onEdit,
    onDone,
    onCancel,
    onRevert,
    onCopy,
    onExpand,
    testIdPrefix = 'artifact-chrome',
    className,
  } = props

  const showEdit = canEdit && !isEditing && Boolean(onEdit)

  function handleEditClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (onEdit) {
      onEdit()
    }
  }

  function handleCopyClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (onCopy) {
      onCopy()
    }
  }

  function handleExpandClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (onExpand) {
      onExpand()
    }
  }

  function handleDoneClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (onDone) {
      onDone()
    }
  }

  function handleCancelClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (onCancel) {
      onCancel()
    }
  }

  function handleRevertClick(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    if (onRevert) {
      onRevert()
    }
  }

  const editingButtons =
    isEditing && canEdit ? (
      <>
        {onRevert ? (
          <button
            type="button"
            className={ICON_BUTTON_CLASS}
            data-testid={`${testIdPrefix}-revert`}
            aria-label={`Revert ${accessibleName} to original`}
            onClick={handleRevertClick}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          className={ICON_BUTTON_CLASS}
          data-testid={`${testIdPrefix}-cancel`}
          aria-label={`Cancel editing ${accessibleName}`}
          onClick={handleCancelClick}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          className={ICON_BUTTON_CLASS}
          data-testid={`${testIdPrefix}-done`}
          aria-label={`Done editing ${accessibleName}`}
          onClick={handleDoneClick}
        >
          <Check className="h-3.5 w-3.5" aria-hidden />
        </button>
      </>
    ) : null

  const editButton = showEdit ? (
    <button
      type="button"
      className={ICON_BUTTON_CLASS}
      data-testid={`${testIdPrefix}-edit`}
      aria-label={`Edit ${accessibleName}`}
      onClick={handleEditClick}
    >
      <Pencil className="h-3.5 w-3.5" aria-hidden />
    </button>
  ) : null

  const copyButton = onCopy ? (
    <button
      type="button"
      className={ICON_BUTTON_CLASS}
      data-testid={`${testIdPrefix}-copy`}
      aria-label={hasCopied ? `Copied ${accessibleName}` : `Copy ${accessibleName}`}
      onClick={handleCopyClick}
    >
      {hasCopied ? (
        <Check className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  ) : null

  const expandButton = onExpand ? (
    <button
      type="button"
      className={ICON_BUTTON_CLASS}
      data-testid={`${testIdPrefix}-expand`}
      aria-label={`Expand ${accessibleName}`}
      onClick={handleExpandClick}
    >
      <Maximize2 className="h-3.5 w-3.5" aria-hidden />
    </button>
  ) : null

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {editingButtons}
      {editButton}
      {copyButton}
      {expandButton}
    </div>
  )
}
